/**
 * SimFab — entry point.
 *
 * Wires the four layers together:
 *
 *   sim/       the solver stack, running whether or not anyone is watching
 *   fab/       the cleanroom the student walks through
 *   tools/     the tool panels, one per curriculum unit
 *   assistant/ question -> experiment -> result -> sharper question
 *
 * The process keeps running while the panel is shut, which is why the sim
 * tick lives here (registered on the frameBus) rather than inside any panel.
 * The renderer, scene, and frame clock belong to the react-three-fiber
 * canvas (fab/react/FabCanvas); this file is the composition root that wires
 * the world to the shell once the canvas reports ready.
 */

import * as THREE from 'three'
import './ui/styles.css'

import { auth } from './auth/Auth'
import { FabScene } from './fab/FabScene'
import { HmiScreens, type HmiReadout } from './fab/hmi'
import { Player } from './fab/Player'
import { getTool, toolProcess, TOOLS, type Tool } from './fab/layout'
import type { Place } from './fab/places'
import { clearPlace, loadPlace, savePlace, savePlaceAs, type StoredPlace } from './fab/place'
import { onHallChange, type HallLayout } from './fab/hall'
import { BOOT_TIMING } from './fab/bootTiming'
import { EtchSim } from './sim/EtchSim'
import { SweepRunner } from './sim/SweepRunner'
import { SolverRunner } from './sim/SolverRunner'
import { Journal, waferInSpec } from './learn/Journal'
import { attachSession } from './learn/SessionStore'
import { missionState } from './learn/missionState'
import { missionOnFloor } from './learn/floor'
import { acceptFromUrl } from './ui/WorkOrders'
import { HouseBus, houseFault } from './sim/house/model'
import { NOMINAL_MAU_CONDITION } from './sim/facilities/MauSim'
import { mauStore } from './sim/facilities/mauStore'
import { Carrier, stepText } from './sim/wafer/model'
import { collectPanelStates, peekPanelReadout, preloadToolPanel } from './tools/registry'
import { talkStore } from './talk/talkStore'
import { briefFor } from './talk/brief'
import { talkWatch } from './talk/watch'
import type { ProcessState } from './tools/types'
import { onLightingChange } from './fab/lighting'
import { onQualityChange, resolvedTier } from './fab/quality'
import { WaferMap } from './ui/WaferMap'
import { el } from './ui/dom'
import { clamp } from './core/math'
import { t, tOr } from './i18n/i18n'
import type { VRMode } from './xr/VRMode'
import { FabCanvas, type FabWorld } from './fab/react/FabCanvas'
import { frameBus } from './app/frame/frameBus'
import { setupAssistantRouting } from './assistant/llm'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import '@ant-design/v5-patch-for-react-19'
import { Shell } from './app/Shell'
import { applyThemeTokens } from './ui/palette'
import { bindWalkTarget, setEntered, walkEntered } from './app/input/walkLock'
import { panelStore } from './app/panel/panelStore'
import { dialogStore } from './app/ui/dialogStore'
import { hudBridge, openButtonBridge, type HudCard } from './app/hud/hudBridge'
import type { VehicleRef } from './fab/builders'
import { touchInput } from './ui/inputMode'
import { splashStore } from './app/shell/splashStore'
import { bindLeaveToEntrance, takeEnterOnLoad } from './app/shell/entrance'
import { hideLoading } from './app/shell/loading'
import { vrBridge } from './app/shell/vrBridge'
import { isTypingTarget } from './core/typing'

// Where the boot's time goes, for the question "why does loading take so
// long". Four marks - here, React mounting, the world built, the first
// frame drawn - and a dev-only line in the console with the split between
// them. The gap before the first mark is the module graph itself, which in
// dev is hundreds of separate requests and in the built app is one file.
if (BOOT_TIMING) performance.mark('boot:script')

// Decide who routes questions before any module lazily builds its assistant.
// A no-op unless VITE_ASSISTANT_LLM=on, in which case every panel picks it up.
setupAssistantRouting()

// palette.ts is the single token source; the :root literals in styles.css
// are only the no-JS fallback.
applyThemeTokens()

const app = document.getElementById('app')!

// --------------------------------------------------------------- rendering

/** One canvas per tool screen, painted for whichever tools are within reading distance. */
const hmiScreens = new HmiScreens()

// The walk controller exists from boot; its input binds to the canvas the
// moment react-three-fiber creates it (FabCanvas does the binding).
const player = new Player()
// The walk-lock is the one owner of player.enabled from here on.
bindWalkTarget(player)

/**
 * The 3D world, assigned when the React canvas mounts (onWorldReady below).
 * Definite-assignment on purpose: everything that touches these runs behind
 * entry or the frame loop, both of which onWorldReady gates.
 */
let fab!: FabScene
/** The canvas's world, kept so a hall can be rebuilt into it (rebuildHall). */
let canvasWorld!: FabWorld
let vr!: VRMode
let glCanvas!: HTMLCanvasElement

// ---------------------------------------------------------------- the sim

const sim = new EtchSim()
const runner = new SweepRunner()
const solvers = new SolverRunner()

const process: ProcessState = { running: false, speed: 5 }

/** One record per session, shared by every module. */
const journal = new Journal()

/**
 * One house per session, shared by every module.
 *
 * It lives here rather than in a module because that is what it is: the
 * building, not a tool. Facilities arms it; every tool feels whatever it is
 * plumbed into.
 */
const house = new HouseBus()
// The make-up air bench shows the unit in the condition the house has put it
// in: arm the loaded pre-filters at FAC-06 and the bench downstairs derates.
house.subscribe((id) => {
  const fault = id ? houseFault(id) : null
  mauStore.setCondition(fault?.mau ?? NOMINAL_MAU_CONDITION)
})

/**
 * One lot in transit per session.
 *
 * The wafer is a fact about the fab rather than about whichever panel is open,
 * so it lives here beside the journal and the house. A tool puts a wafer in;
 * the next tool down the aisle finds it there.
 */
const carrier = new Carrier()
sim.onWaferLoaded = () => {
  waferLogged = false
}

// Yesterday's shift, if there was one. Before any panel is built, so restored
// panel state is waiting when each panel is first constructed.
attachSession({ journal, carrier, collectPanelStates })

/** Wafer map used only to texture the wafer inside the 3D chamber. */
const chamberMap = new WaferMap(256)

// ---------------------------------------------------------- the Open button

// The OPEN button itself is React (app/hud/Hud.tsx); the loop only tells it
// where to sit. What a press does is the floor's business, so it lives here.
openButtonBridge.onPress = () => {
  const target = selection()
  if (target && !panelStore.open) {
    selected = null
    panelStore.openTool(target)
  }
}

/** Scratch for projecting the screen anchor into page coordinates. */
const openButtonAnchor = new THREE.Vector3()

function placeOpenButton(target: Tool | null): void {
  if (!target || !fab.hmiScreenWorld(target.id, openButtonAnchor)) {
    openButtonBridge.hide()
    return
  }
  // Float above the physical screen. Behind the camera, or off past the
  // viewport edge, it hides rather than swims across unrelated geometry.
  openButtonAnchor.y += 0.55
  openButtonAnchor.project(player.camera)
  if (openButtonAnchor.z > 1 || Math.abs(openButtonAnchor.x) > 0.95 || Math.abs(openButtonAnchor.y) > 0.95) {
    openButtonBridge.hide()
    return
  }
  openButtonBridge.show(
    ((openButtonAnchor.x + 1) / 2) * window.innerWidth,
    ((1 - openButtonAnchor.y) / 2) * window.innerHeight,
  )
}

// ----------------------------------------------------------------- panel
//
// The chrome is React (app/panel/ToolPanelFrame); the store owns open/close.
// onWorldReady wires the store's services and the glide below.

/**
 * Land at a named tool, from wherever the student is.
 *
 * Same destination and pacing as the touch glide below: the tool's HMI
 * viewpoint, at brisk-walk speed, honouring reduced motion. If a panel is open
 * it closes first, so 'jump to MET-05 from inside DEP-02' is one action.
 */
function jumpToTool(target: Tool | Place): void {
  if (!walkEntered()) return
  panelStore.closeTool()
  // A place carries its own viewpoint; a tool's is derived from where it stands.
  const view =
    'assetTag' in target
      ? fab.hmiViewpoint(target)
      : { eye: new THREE.Vector3(...target.eye), target: new THREE.Vector3(...target.target) }
  const distance = player.position.distanceTo(view.eye)
  const duration = REDUCED_MOTION ? 0.05 : clamp(distance * 0.09, 0.5, 2.4)
  player.focusOn(view.eye, view.target, duration)
}

// The work-order runner: arms what an accepted order asks for, and derives
// completion from the journal. The chip is its live face; the debrief fires
// once per completion with the same itemised hours the shift debrief uses.
missionState.attach(journal, house)
missionState.onComplete = (progress) => {
  dialogStore.open({
    kind: 'orderDebrief',
    progress,
    journal,
    startIndex: missionState.sliceSince(),
  })
}
// A class link (?order=<id>) hands this browser the order the instructor
// picked - the whole classroom armed with one URL and no server.
acceptFromUrl()

// ---------------------------------------------------------------- splash

/** Respect the OS setting - the entry drift is decoration, not information. */
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches

// Pull in the auth SDK now. The corner control is on screen from the entry
// screen onward, so it needs to know whether anyone is already signed in - and
// the seconds a student spends reading that screen are exactly the budget the
// round trip needs. Nothing waits on it: the cleanroom is already rendering and
// the solver is already ticking while it loads.
void auth.start()

/**
 * A returning student does not re-enter through the airlock. If a saved place
 * exists (see fab/place.ts) the splash is never built: they stand back up
 * exactly where the refresh knocked them down - same spot, same gaze, same
 * open panel. The airlock stays for first visits, where it is the keys
 * legend, and for places old enough to have expired. Runs from onWorldReady,
 * because reopening a panel glides the eye to the tool's HMI - which needs
 * the world.
 */
/**
 * Dev only: the boot's time, in the console, once the first frame is drawn.
 * Read it with the tab in the FOREGROUND - a background tab gets no frames
 * and the last leg stretches to however long it sat there.
 */
function reportBoot(): void {
  performance.mark('boot:frame')
  const at = (name: string) => performance.getEntriesByName(name)[0]?.startTime ?? NaN
  const leg = (label: string, from: number, to: number) => `${label} ${Math.round(to - from)} ms`
  const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
  const t0 = nav ? nav.startTime : 0
  // The hall's own stages, from the marks FabScene's constructor drops.
  const stages = performance
    .getEntriesByType('mark')
    .filter((m) => m.name.startsWith('boot:fab:'))
    .sort((x, y) => x.startTime - y.startTime)
  let prev = at('boot:render')
  const split = stages.map((m) => {
    const line = leg(m.name.slice('boot:fab:'.length), prev, m.startTime)
    prev = m.startTime
    return line
  })
  if (split.length) console.info('[boot:hall] ' + split.join(' | '))
  // And the prewarm's, from the marks FabScene and FabCanvas drop inside it:
  // scene compile per tier, the pipeline's variants, the throwaway render.
  const pre = performance
    .getEntriesByType('mark')
    .filter((m) => m.name.startsWith('boot:pre:') && m.startTime < at('boot:compiled'))
    .sort((x, y) => x.startTime - y.startTime)
  let prevPre = at('boot:world')
  const preSplit = pre.map((m) => {
    const line = leg(m.name.slice('boot:pre:'.length), prevPre, m.startTime)
    prevPre = m.startTime
    return line
  })
  if (preSplit.length) console.info('[boot:prewarm] ' + preSplit.join(' | '))
  console.info(
    '[boot] ' +
      [
        leg('modules', t0, at('boot:script')),
        leg('to React', at('boot:script'), at('boot:render')),
        leg('build hall', at('boot:render'), at('boot:world')),
        leg('shaders', at('boot:world'), at('boot:compiled')),
        leg('first frame', at('boot:compiled'), at('boot:frame')),
        leg('TOTAL', t0, at('boot:frame')),
        // A sample from a hidden tab measures the browser's throttling, not
        // the boot; say so on the line rather than leaving it to be noticed.
        `tab ${document.visibilityState}`,
      ].join(' | '),
  )
}

function restoreOrSplash(): void {
  const restoredPlace = loadPlace()
  if (restoredPlace) {
    setEntered(true)
    // Standing below the slab: the room has to exist before the pose is set
    // - and it is built here, before the canvas prewarms, so its programs
    // compile with everything else rather than on the first step.
    if (restoredPlace.y < 0) fab.ensureSubfab()
    player.setPose(
      new THREE.Vector3(restoredPlace.x, restoredPlace.y, restoredPlace.z),
      restoredPlace.yaw,
      restoredPlace.pitch,
    )
    if (restoredPlace.tool) {
      const tool = getTool(restoredPlace.tool)
      // Reopening tweens the eye to the HMI, which is where closing the panel
      // would have left them anyway.
      if (tool) panelStore.openTool(tool)
    }
  } else if (takeEnterOnLoad()) {
    // Chosen from the other fab's airlock, which they have already read:
    // straight in, standing where its entry dolly would have left them.
    setEntered(true)
    player.standAtEntry()
  } else {
    // The React splash (app/shell/Splash.tsx) takes it from here; entering
    // sets the walk-lock's flag, which every cluster watches.
    splashStore.show()
  }
}

/**
 * Back out through the airlock without leaving the page. The hall stays
 * built and the solvers keep ticking; what changes is the student's state:
 * nothing open, not entered, standing at the top of the entry dolly, and no
 * saved place - so a refresh from the airlock is a first visit, not a
 * teleport back to wherever they were. The shift itself (journal, lot, tool
 * settings) is untouched; that is "Start a new session", a different item.
 */
function leaveToEntrance(): void {
  if (!walkEntered()) return
  panelStore.closeTool()
  talkStore.close()
  dialogStore.close()
  clearPlace()
  player.restartDrift()
  // Order matters: the entered flag drops first so the frame loop hands the
  // camera to the drift before the splash paints over it.
  setEntered(false)
  splashStore.show()
}

// Where the student stands, written on the same throttled clock that already
// paints the scene - and once more on the way out, so the last footstep before
// a refresh is the one that gets kept.
let placeSaveAccumulator = 0
function rememberPlace(dt: number): void {
  if (!walkEntered()) return
  placeSaveAccumulator += dt
  if (placeSaveAccumulator < 2) return
  placeSaveAccumulator = 0
  writePlace()
}
/** Where the student stands this instant, as the place store keeps it. */
function currentPlace(): Omit<StoredPlace, 'savedAt'> {
  const p = player.yawObject.position
  return {
    x: p.x,
    y: p.y,
    z: p.z,
    yaw: player.yawObject.rotation.y,
    pitch: player.camera.rotation.x,
    tool: panelStore.open?.id ?? null,
  }
}
function writePlace(): void {
  if (!walkEntered()) return
  savePlace(currentPlace())
}

/**
 * Switch halls without leaving the page.
 *
 * The layout binding has already flipped (fab/hall.ts setHall) when this is
 * told. It files the pose under the fab being LEFT, builds the new hall
 * against the new layout - the same renderer, so every shader program it
 * needs is already compiled and cached - hands the people and the sub-fab
 * across, swaps it in under the canvas, and only then tears the old hall
 * down, so the programs the two share are never released in between. Then
 * it stands the student where they last were in the destination fab, or
 * where they were standing a moment ago if they have never been there
 * (place.ts carryPlaceTo). The loading screen went up when the menu was
 * clicked and comes down on the first frame the new hall draws.
 */
function rebuildHall(to: HallLayout, from: HallLayout): void {
  const t0 = performance.now()
  const phases: Array<[string, number]> = []
  const phase = (name: string): void => {
    phases.push([name, performance.now()])
  }
  if (walkEntered()) savePlaceAs(from, currentPlace())
  panelStore.closeTool()
  selected = null
  selectedVehicle = null
  const old = fab
  const carried = old.detach()
  // Out of the scene before the new hall is built, so its capture and its
  // prewarm see one rig's lamps and not two - see FabScene.unmount. The
  // materials and their programs stay alive until dispose, below.
  old.unmount()
  phase('detach')
  const next = new FabScene(canvasWorld.scene, hmiScreens, canvasWorld.gl)
  phase('build')
  if (carried.subfab) next.adoptSubfab(carried.subfab)
  if (carried.people) next.addPeople(carried.people)
  if (carried.subfabCrew) next.addSubfabCrew(carried.subfabCrew)
  if (carried.chaseCrew) next.addChaseCrew(carried.chaseCrew)
  next.setQualityTier(resolvedTier())
  // The boot shrinks the key light's shadow box to the student's surroundings
  // once its prewarm is done (FabCanvas). A hall built here starts with the
  // rig's own box, and nothing else ever shrinks it - every frame after the
  // switch would draw the whole building's shadow map.
  next.setShadowScope('eye')
  phase('adopt')
  canvasWorld.fab = next
  fab = next
  // Compile only, and BEFORE the old hall is disposed: a shader program is
  // released when the last material using it goes, so the new hall's
  // materials have to take hold of the programs first or the old hall's
  // disposal deletes the lot and this recompiles all of them cold. With
  // that order it is a traversal plus whatever the other layout's
  // materials add, and no throwaway render - the hall uploads as it is
  // seen, like after a boot.
  next.prewarmLighting(player.camera, player.yawObject.position, () => {
    canvasWorld.postfx.compileSceneVariants()
    // The boot's throwaway render, here too: it uploads and first-draws
    // what is in view behind the loading screen. Without it the first real
    // frames after a switch did that in front of the student, which read
    // as the hall waking up and then crawling.
    canvasWorld.postfx.compilePasses()
  })
  phase('prewarm')
  old.dispose()
  phase('dispose')
  player.setColliders(next.colliders())
  const place = loadPlace()
  if (place) {
    player.setPose(new THREE.Vector3(place.x, place.y, place.z), place.yaw, place.pitch)
  } else if (walkEntered()) {
    // No place in this fab - which between Fab A and Fab B never happens,
    // because the switch carries one over, and in a fab somebody built
    // always does (fab/place.ts `carryPlaceTo`). Standing still would leave
    // them at the old hall's coordinates in a hall of a different length
    // with a different row: inside a mainframe, or past the end wall. The
    // airlock is where a floor they have never seen begins.
    player.standAtEntry()
  }
  updateScene()
  const off = frameBus.onFrame(() => {
    off()
    requestAnimationFrame(hideLoading)
  })
  phase('place')
  if (BOOT_TIMING) {
    let prev = t0
    const split = phases.map(([name, at]) => {
      const line = `${name} ${Math.round(at - prev)} ms`
      prev = at
      return line
    })
    console.info(`[switch] ${from} -> ${to} in ${Math.round(performance.now() - t0)} ms | ${split.join(' | ')} | tab ${document.visibilityState}`)
  }
}
window.addEventListener('beforeunload', writePlace)

// ----------------------------------------------------------------- input

window.addEventListener('keydown', (e) => {
  // A shell dialog owns the keyboard while it is up - F/E must not act
  // underneath it. Escape closes it HERE, not only in antd: antd's own
  // Escape needs focus inside the modal, and a student whose focus stayed
  // on the floor would otherwise find the key dead.
  if (dialogStore.current) {
    if (e.code === 'Escape') dialogStore.close()
    return
  }
  // The confirm dialogs live outside the dialog store; antd owns them fully.
  if (document.querySelector('.ant-modal-wrap')) return
  if (e.code === 'Escape' && talkStore.open) {
    // One key, two jobs, innermost first: Escape stops what the technician
    // is doing this second - a reply on its way, or one being spoken - and
    // only ends the conversation when there is nothing left to stop. That
    // is the ladder an editor or a browser uses, so it needs no learning,
    // and it is why cancelling a sent question needs no second key.
    if (!talkStore.stop()) talkStore.close()
    return
  }
  // While a conversation is up the keyboard is the text box's, wherever
  // focus happens to be: a click on the floor beside the panel blurs the
  // box, and the next "e" typed must not open the tool behind the person.
  if (talkStore.open) return
  if (e.code === 'Escape' && panelStore.open) {
    panelStore.closeTool()
    return
  }
  if (e.code === 'Escape' && (selected || selectedVehicle)) {
    selected = null
    selectedVehicle = null
    return
  }
  // A keypress aimed at a form control belongs to that control, not to the
  // floor - same rule the splash applies to its own shortcuts.
  if (isTypingTarget(e.target)) return
  if (e.code === 'KeyF' && walkEntered()) {
    e.preventDefault()
    dialogStore.open({ kind: 'finder', onJump: jumpToTool })
    return
  }
  if (e.code === 'KeyE' && !panelStore.open && walkEntered()) {
    // The selected tool first; failing that, whatever is under the cursor.
    const target = selection() ?? currentTarget()
    if (target) {
      selected = null
      panelStore.openTool(target)
    }
  }
})

// Clicking a tool selects it; clicking it again opens it. The click only
// fires if the press did not travel, so ending a look-drag over a tool never
// selects it by accident.
player.onClick = (ndc, { touch }) => {
  if (!walkEntered() || panelStore.open || talkStore.open) return
  // Below the fab floor the clickable things are plant skids, and they are
  // checked first so a skid can never lose its click to a tool trigger picked
  // through the ceiling. Above the floor, skids are unreachable by range.
  // (A click cannot arrive while a dialog is up - the walk-lock has the
  // player disabled - so no open-dialog guard is needed here.)
  if (player.position.y < 0) {
    const skid = fab.pickSkid(ndc, player.camera)
    if (skid) {
      dialogStore.open(skid === 'mau' ? { kind: 'mau' } : { kind: 'skid', system: skid })
      return
    }
  }
  // A technician, before the tool behind them: a click on a person is a
  // conversation (TALK.md), and they turn to have it.
  const person = fab.pickPerson(ndc, player.camera)
  if (person) {
    selected = null
    selectedVehicle = null
    talkStore.openWith(person)
    return
  }
  const hit = fab.pickPointer(ndc, player.camera)
  if (hit) {
    selectedVehicle = null
    if (selected?.id === hit.tool.id) {
      selected = null
      panelStore.openTool(hit.tool)
    } else {
      selected = hit.tool
    }
    return
  }
  // A vehicle or robot: its card, and nothing more.
  const vehicle = fab.pickVehicle(ndc, player.camera)
  if (vehicle) {
    selected = null
    selectedVehicle = vehicle
    return
  }
  // A click on nothing is a click to be left alone.
  selected = null
  selectedVehicle = null

  // A tap on a tool beyond arm's reach is how a phone gets around: no keyboard
  // means no WASD, so the tap is the walk. The glide lands at the tool's HMI
  // viewpoint - the same place closing a panel leaves you - so a second tap
  // opens it. Mouse users are deliberately excluded: they have a keyboard, and
  // a stray click teleporting them across the fab would be worse than nothing.
  if (touch) {
    const far = fab.pickPointer(ndc, player.camera, Infinity, true)
    if (far) {
      const view = fab.hmiViewpoint(far.tool)
      const distance = player.position.distanceTo(view.eye)
      // Paced like a brisk walk, not a cut - unless the OS asks for no motion.
      const duration = REDUCED_MOTION ? 0.05 : clamp(distance * 0.09, 0.5, 2.4)
      player.focusOn(view.eye, view.target, duration)
    }
  }
}

const ray = new THREE.Ray()

/**
 * What the student is pointing at. Falls back to what is straight ahead when
 * the cursor has left the viewport, so the keyboard shortcut still works.
 */
function currentTarget(range?: number): Tool | null {
  if (player.pointerInside && !player.dragging) {
    return fab.pickPointer(player.pointer, player.camera, range)?.tool ?? null
  }
  player.forwardRay(ray)
  return fab.pick(ray.origin, ray.direction, range)?.tool ?? null
}

// ------------------------------------------------------------ selection
//
// The approach card and the floating OPEN button announce ONE tool: the one
// the student selected by clicking it. Nothing hover-driven, however
// gated - a card that can appear because the cursor happened to rest on a
// nearby machine is a card that appears by accident, and walking an aisle
// lined with machines is nothing but nearby machines. Selecting is the act;
// the card is its receipt.
//
// Click a tool: it is selected, and its card and OPEN button appear. Click
// it again, press OPEN, or press E: it opens. Click the floor, press Escape,
// or walk out of reach: the selection clears. E on an unselected tool still
// opens it straight away - the keyboard is the expert path and needs no
// receipt. The cursor's pointer shape over a tool is untouched; it costs
// nothing and says only "clickable".

let selected: Tool | null = null
const selectedPos = new THREE.Vector3()

/** The selection survives only as long as the tool is within a click's reach. */
function selection(): Tool | null {
  if (!selected) return null
  selectedPos.set(...selected.position)
  selectedPos.y = player.position.y
  if (selectedPos.distanceTo(player.position) > SELECTION_REACH) selected = null
  return selected
}

// A clicked vehicle or robot is the other thing a card can be about. The
// card is composed HERE, every frame, from the vehicle's live status - who
// it is, what it carries, where it is going, what it is doing right now -
// because that status changes as the student watches: a vehicle they
// clicked while it cruised will hold, transfer, and pick a new destination
// while the card is up. Reading that is the automated-material-handling
// lesson: why a lot has not arrived yet. Nothing opens; the card is all.
// It clears the way a tool's does, and when the vehicle drives out of reach.
let selectedVehicle: VehicleRef | null = null

function vehicleCard(): HudCard | null {
  if (!selectedVehicle) return null
  fab.vehiclePosition(selectedVehicle, selectedPos)
  selectedPos.y = player.position.y
  if (selectedPos.distanceTo(player.position) > SELECTION_REACH) {
    selectedVehicle = null
    return null
  }
  const s = fab.vehicleStatus(selectedVehicle)
  const stateKey = {
    cruise: 'vehicle.Cruising',
    hold: 'vehicle.Holding',
    transfer: 'vehicle.Transferring',
    move: 'vehicle.Moving',
    deliver: 'vehicle.Delivering',
    yield: 'vehicle.Yielding',
  }[s.state]
  const bound = s.destination
    ? t('vehicle.BoundFor', { tool: s.destination.assetTag, port: s.port })
    : t('vehicle.BoundEnd')
  return {
    tag: `${s.id} · ${t(s.kind === 'oht' ? 'vehicle.Hoist' : 'vehicle.Robot')}`,
    name: t('vehicle.Carrying', { lot: s.lot, carrier: s.carrier }),
    blurb: `${bound} · ${t(stateKey, { speed: s.speed.toFixed(1) })}`,
    keyHtml: t(touchInput() ? 'vehicle.DismissTouch' : 'vehicle.Dismiss'),
  }
}

/**
 * A little past pick range: the pick measures to the nearest face, this
 * measures to the tool's centre, and the widest tools are 6 m across.
 */
const SELECTION_REACH = 15

// ------------------------------------------------------------------ loop
//
// The clock itself lives in FabCanvas (react-three-fiber's advance, driven
// through gl.setAnimationLoop so a headset's own frame timing takes over in
// VR). What follows are the frameBus subscribers - everything main's old
// frame() did outside the 3D layer, registered once the world reports ready.

/** The solver ticks at a fixed rate, decoupled from the frame rate. */
const SIM_HZ = 20
let simAccumulator = 0
/** Guards against logging the same finished wafer on every frame. */
let waferLogged = false
/** Whether the last tick found the student below the fab floor. */
let wasBelow = false

/** Advance the process on its own clock. Every frame. */
function simTick(dt: number): void {
  if (!process.running) return
  simAccumulator += dt
  const tick = 1 / SIM_HZ
  let steps = 0
  while (simAccumulator >= tick && steps < 4) {
    sim.step(tick * process.speed)
    simAccumulator -= tick
    steps++
  }
  if (steps === 4) simAccumulator = 0

  // A wafer that has fully cleared is a finished wafer. Log it once, with
  // whether it would actually have shipped - that count is what makes the
  // cost of a slow diagnosis concrete later.
  const m = sim.metrics()
  if (!waferLogged && m.clearedFraction >= 0.999) {
    waferLogged = true
    process.running = false
    journal.record({
      kind: 'wafer',
      rateNmMin: m.meanRateNmMin,
      nonUniformityPct: m.rateNonUniformityPct,
      inSpec: waferInSpec(m.meanRateNmMin, m.rateNonUniformityPct),
    })
  }
}

/** Push solver state into the scene, a few times a second (the slow tick). */
function slowWork(dt: number): void {
  rememberPlace(dt)
  // A held tool still has a state. Re-deriving it while paused is what makes
  // a slider move the readouts before the student ever presses start, and it
  // is nearly free when nothing changed - the solver exits on tolerance.
  if (!process.running) sim.refresh()
  // Only the bays near the student stay lit - see FabScene.updateLights.
  fab.updateLights(player.yawObject.position)
  // The sub-fab's beacons and utility board track the live house state -
  // cheap here, because the board only repaints when its text changes.
  fab.updateSubfab(house.state, house.armed)
  // Crossing below the fab floor is evidence too - "take the stairs" is an
  // objective on more than one work order.
  const below = player.yawObject.position.y < 0
  if (below && !wasBelow) journal.record({ kind: 'place', where: 'subfab' })
  wasBelow = below
  updateScene()
  updateNearbyHmis()
  if (!panelStore.open) updateHudStatus()
}

/** The open panel's tick, or the floor's targeting chrome. Every frame. */
function overlayTick(_dt: number): void {
  const open = panelStore.open
  if (open) {
    // The open panel ticks itself: a mounted panel registers its own frame
    // callback with the bus (see ui/kit/useFrameTick), so there is nothing
    // to drive from here.
    openButtonBridge.hide()
  } else if (!vr.active) {
    const target = walkEntered() ? currentTarget() : null
    const shown = walkEntered() ? selection() : null
    if (shown) {
      hudBridge.setTarget(shown)
    } else {
      // A vehicle's card, or nothing. Through setCard rather than
      // setTarget: the HUD's own replay-on-language-change is for tools.
      hudBridge.setCard(walkEntered() ? vehicleCard() : null)
    }
    placeOpenButton(shown)
    const vehicleUnderCursor = !target && walkEntered() && !player.dragging && player.pointerInside
      ? fab.pickVehicle(player.pointer, player.camera) ?? fab.pickPerson(player.pointer, player.camera)
      : null
    // Fetch the CODE for what they are looking at before they ask for it, so
    // a tool opens on the click rather than after it. The panel itself is not
    // built until the open - its constructor is main-thread solver work, and
    // paying that because a gaze crossed a tool is what made walking hitch.
    if (target) preloadToolPanel(target.id)
    // The cursor itself says whether there is something to click.
    glCanvas.style.cursor = player.dragging
      ? 'grabbing'
      : target || vehicleUnderCursor
        ? 'pointer'
        : 'default'
  }
}

/**
 * The canvas has a world. Wire everything that needed one, register the
 * frame work, and stand the student up. Guarded because StrictMode re-runs
 * mount effects in development.
 */
let worldReady = false
function onWorldReady(world: FabWorld): void {
  if (worldReady) return
  worldReady = true
  // The hall, the VR mode and the post pipeline are constructed; the shader
  // prewarm has not run yet (it is the next layout effect in FabCanvas).
  if (BOOT_TIMING) performance.mark('boot:world')
  fab = world.fab
  vr = world.vr
  glCanvas = world.gl.domElement
  canvasWorld = world
  // A hall choice rebuilds the hall on the spot - see rebuildHall.
  onHallChange(rebuildHall)
  // An order armed against a station that the new floor has not got cannot
  // complete and its fault has nothing to sit on. Abandoned, the way a
  // student would abandon it; the board still lists it, marked.
  onHallChange(() => {
    const active = missionState.activeMission
    if (active && !missionOnFloor(active)) missionState.abandon()
  })

  // A lighting choice from the main menu repaints the hall on the spot.
  onLightingChange(() => fab.applyLighting())
  // Graphics quality: the governor measures this machine (or obeys a manual
  // choice) and the scene flips its extra downlights accordingly. Both
  // tiers' shaders were prewarmed by the canvas, so this never compiles.
  fab.setQualityTier(resolvedTier())
  onQualityChange(() => fab.setQualityTier(resolvedTier()))

  vrBridge.init(world.vr, world.gl)

  // The technicians can now be talked to: the store turns them through the
  // scene, and reads each one's brief off the same screens the aisle paints.
  talkStore.init({
    engage: (id) => fab.engagePerson(id),
    disengage: () => fab.disengagePerson(),
    setTalking: (on) => fab.setPersonTalking(on),
    brief: (id) =>
      briefFor(id, {
        readoutFor: (tool) => readoutOf(tool),
        houseArmed: () => house.armed,
        whereIs: (person) => fab.personWhere(person),
        journal,
        lastReport: () => talkStore.lastReport,
      }),
    // A call the student made before the tool reported, with what it then
    // measured beside it. Journalled here rather than in the store so the
    // talk layer stays clear of the learn layer, and the tool it is tagged
    // with is whoever's tool she was standing at - the same way every other
    // event in the record knows where it happened.
    predicted: (about, said, measured) =>
      journal.record({
        kind: 'prediction',
        about,
        said,
        measured,
        toolId: talkStore.open?.toolId ?? undefined,
      }),
    // And what they said when she asked why it went that way. Recorded right
    // after the call it belongs to, which is how the debrief pairs them.
    explained: (about, said) =>
      journal.record({
        kind: 'explanation',
        about,
        said,
        toolId: talkStore.open?.toolId ?? undefined,
      }),
  })

  // The panel store can now glide the eye and hand panels their services.
  panelStore.init({ sim, runner, solvers, process, journal, house, carrier }, (tool) => {
    const view = fab.hmiViewpoint(tool)
    player.focusOn(view.eye, view.target, 0.55)
  })

  // The one thing on this floor that speaks first. Only the journal feeds
  // it, so nothing it raises can be something the record does not hold.
  talkWatch.start(journal)

  frameBus.onFrame(simTick)
  frameBus.onFrame(overlayTick)
  frameBus.onSlow(slowWork)

  restoreOrSplash()
  bindLeaveToEntrance(leaveToEntrance)
  updateScene()
  updateHudStatus()

  // The loading screen from index.html comes down once the hall has actually
  // been drawn. A tick runs BEFORE its frame's draw, so hiding inside it would
  // fade over an unpainted canvas; a requestAnimationFrame scheduled from the
  // tick fires after that frame has been presented, which is the moment. One
  // frame is all it needs - a background tab may not get a second for a long
  // time. See app/shell/loading.ts for why the screen is not React.
  const offLoading = frameBus.onFrame(() => {
    offLoading()
    requestAnimationFrame(hideLoading)
    if (BOOT_TIMING) reportBoot()
  })
  // And a belt to that brace. A background tab gets no animation frames at
  // all, so a page that loads there - a Vite full reload behind another
  // window, say - would sit under the screen until it was looked at. By now
  // the hall is built and the only thing left to wait for is the draw; if
  // that has not happened in a second and a half, nobody is watching the
  // canvas anyway, and `hideLoading` is idempotent so the two never fight.
  setTimeout(hideLoading, 1500)

  // Dev-only handle for driving the fab from the console or a browser
  // harness - and classroom demonstration.
  if (import.meta.env.DEV) {
    ;(window as unknown as Record<string, unknown>).__simfab = {
      /** A getter: the hall is rebuilt in place on a fab switch. */
      get fab() {
        return fab
      },
      player,
      panelStore,
      dialogStore,
      postfx: world.postfx,
      /**
       * The conversation store, so a harness can open one and ask a
       * question without having to walk up to somebody and raycast a
       * click at them. `openWith('sarah')` then `ask('...')`.
       */
      talk: talkStore,
      /**
       * The brief any one of them would speak from, right now - the same
       * one `ask` builds, through the same hooks. Readable so that what a
       * technician is ALLOWED to know can be asserted without spending a
       * model call to find out what they chose to say about it.
       */
      brief: (id: string) =>
        briefFor(id, {
          readoutFor: (tool) => readoutOf(tool),
          houseArmed: () => house.armed,
          whereIs: (person) => fab.personWhere(person),
          journal,
          lastReport: () => talkStore.lastReport,
        }),
      /** The one thing that speaks first, so a harness can see what it raised. */
      watch: talkWatch,
      /** What the floor has selected - readable so a harness can assert it. */
      selection: () => ({ tool: selected, vehicle: selectedVehicle }),
    }
  }
  Object.assign(window as unknown as Record<string, unknown>, { sim, fab, player, runner, solvers, journal, house, carrier, TOOLS, missionState })
}

/**
 * Paint the screen on every tool the student can actually read.
 *
 * A real tool's HMI is never off, and a student scanning the aisle should be
 * able to see which tools are running before deciding which to walk to. That is
 * what the status tower does on real hardware; this puts the numbers next to it.
 *
 * Bounded two ways, because this runs several times a second for the life of
 * the session. Only tools within reading distance are painted at all, and a
 * panel is only asked for its readout if it has ALREADY been built - going
 * through `getToolPanel` here would construct a solver and a set of chart
 * canvases for every tool a student walked past.
 */
const HMI_READ_RANGE = 16

/** Tools whose screen paint threw once - skipped thereafter, and said so once. */
const hmiFailed = new Set<string>()

function updateNearbyHmis(): void {
  const eye = player.yawObject.position
  for (const tool of TOOLS) {
    const dx = tool.position[0] - eye.x
    const dz = tool.position[2] - eye.z
    if (dx * dx + dz * dz > HMI_READ_RANGE * HMI_READ_RANGE) continue
    if (hmiFailed.has(tool.id)) continue

    // A screen is decoration, and this runs inside frame(). An exception here
    // - a panel left half-built by a failed mount, anything - must cost one
    // dark screen, never the animation loop: a throw that escapes frame()
    // freezes the whole fab at its last rendered image, with WASD dead and
    // the approach card stuck on screen looking like an unclosable dialog.
    try {
      const readout = readoutOf(tool)
      hmiScreens.paint(tool, toolProcess(tool), readout)
      fab.updateHmi(tool.id, readout, toolProcess(tool))
    } catch (err) {
      hmiFailed.add(tool.id)
      console.error(`HMI paint failed for ${tool.assetTag}; screen disabled`, err)
    }
  }
}

/**
 * What a tool's screen says: its panel's own readout if it has been opened,
 * the floor's if not - and, either way, RUNNING for the tool the lot is
 * sitting at.
 *
 * That last part is the one thing about "running" the floor knows for every
 * tool alike. Only two panels have a process that keeps going on a clock
 * (the etcher, the ALD reactor); for the rest a recipe is solved the moment
 * it is set, and the only honest sense in which the tool is busy is that the
 * lot is in it. The last line of the travel card says where that is. An
 * alarm is never overridden: a tool with a standing fault and the lot in it
 * is a tool in alarm.
 */
function readoutOf(tool: Tool): HmiReadout {
  const own = peekPanelReadout(tool.id)
  const readout = own ?? idleReadout(tool)
  if (readout.state !== 'idle') return readout
  const history = carrier.current?.history
  const here = history && history.length > 0 && history[history.length - 1].tool === tool.assetTag
  if (!here) return readout
  // A restored session can leave the lot at a tool whose panel has not been
  // rebuilt yet: the floor's readout would say IDLE under a green band.
  return { ...readout, state: 'running', headline: own ? readout.headline : tOr('tool.hmi.LotHere', 'Lot in') }
}

/**
 * What a tool shows when its panel has never been opened, so there is no
 * simulation behind it to ask.
 *
 * Deliberately not faked numbers. It reports the two things the floor genuinely
 * knows from outside the tool: that it is idle, and whether the lot currently
 * in the carrier has been through here - which is a real thing a technician
 * reads off a travel card, and which makes the aisle show where the wafer has
 * been.
 */
function idleReadout(tool: Tool): HmiReadout {
  const wafer = carrier.current
  const visited = wafer?.history.filter((h) => h.tool === tool.assetTag) ?? []
  const last = visited[visited.length - 1]
  return {
    state: 'idle',
    headline: t('hmi.Idle'),
    caption: t('hmi.Status'),
    rows: last ? [[t('hmi.LastStep'), stepText(last)]] : [],
  }
}

function updateScene(): void {
  const samples = sim.samples()
  chamberMap.render({
    mode: 'thickness',
    values: samples.map((s) => s.thickness * 1e9),
    unit: 'nm',
    showSites: false,
  })
  fab.updateWafer(chamberMap.fieldCanvas)

  // Plasma brightness tracks the solved discharge, and its colour shifts toward
  // the harder blue-white of a high ion energy - so a student walking the aisle
  // can read the state of the tool before opening anything.
  const p = sim.plasmaState
  const intensity = process.running ? clamp(p.plasmaDensity / 4e17, 0.05, 1) : 0.02
  const hard = clamp(p.ionEnergyEv / 320, 0, 1)
  const colour = new THREE.Color().setHSL(0.56 - 0.06 * hard, 0.75, 0.55 + 0.2 * hard)
  fab.updatePlasma(intensity, colour)
}

function updateHudStatus(): void {
  const m = sim.metrics()

  // The strip reports the etch solver - the one process that keeps running
  // while the student walks elsewhere - and ONLY once that process has meant
  // something to this student: it is running now, or a wafer has actually
  // accumulated etch time. A fresh entry used to see "ETCH-01 idle, 204
  // nm/min" before ever touching a tool, which read as a status of the whole
  // fab, or as the last tool used. It was neither; it was a leftover from
  // when etch was the only live tool.
  if (!process.running && m.processTime <= 0) {
    hudBridge.setStatus([])
    return
  }

  hudBridge.setStatus([
    [
      // Named explicitly: this block reports the etch solver, and the first
      // tool in flow order is now the gowning airlock.
      getTool('etch')?.assetTag ?? 'ETCH-01',
      process.running ? t('hud.Running', { speed: process.speed }) : t('hud.Idle'),
    ],
    [t('hud.Rate'), `${m.meanRateNmMin.toFixed(0)} nm/min`],
    [t('hud.NonUniformity'), `${m.rateNonUniformityPct.toFixed(1)}%`],
    [t('hud.Cleared'), `${(m.clearedFraction * 100).toFixed(0)}%`],
    [t('hud.Time'), `${m.processTime.toFixed(0)} s`],
  ])
}

// ------------------------------------------------------------ React root

// The React half of the app: the 3D canvas itself and the shell around it.
// The vanilla overlays (HUD, panel, buttons) are earlier siblings with
// explicit z-indices, so they stack above the canvas exactly as before.
const reactShell = el('div')
app.appendChild(reactShell)
if (BOOT_TIMING) performance.mark('boot:render')
createRoot(reactShell).render(
  <StrictMode>
    <FabCanvas
      player={player}
      screens={hmiScreens}
      reducedMotion={REDUCED_MOTION}
      isEntered={walkEntered}
      onTick={frameBus.tick}
      onReady={onWorldReady}
    />
    <Shell jumpToTool={jumpToTool} journal={journal} house={house} />
  </StrictMode>,
)
